/** Firestore access for `skidaCrmNotes` — see `notes.ts` for the why. */
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'
import { NOTE_MAX, type Note, type NoteKind } from './notes'

const COLLECTION = 'skidaCrmNotes'

/** How many entries a record's log shows. Enough to be a history, not a feed. */
const PAGE = 50

function mapNote(snap: QueryDocumentSnapshot<DocumentData>): Note {
  const d = snap.data()
  return {
    id: snap.id,
    kind: d.kind as NoteKind,
    recordId: String(d.recordId ?? ''),
    // A note written a second ago has no server timestamp yet — Firestore
    // resolves `serverTimestamp()` on the round trip, and the local echo arrives
    // first. Falling back to now keeps it at the top where the writer expects
    // it, rather than at the bottom under a blank date.
    at: toIsoOrNull(d.at) ?? new Date().toISOString(),
    author: String(d.author ?? 'Unknown'),
    text: String(d.text ?? ''),
    source: d.source === 'migrated' ? 'migrated' : 'user',
  }
}

/**
 * Live, newest-first notes for one record — 91's "the newest sits on top".
 *
 * Returns the unsubscribe function, like `subscribeToActivity` next door.
 */
export function subscribeToNotes(
  kind: NoteKind,
  recordId: string,
  onData: (notes: Note[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    query(
      collection(db, COLLECTION),
      where('kind', '==', kind),
      where('recordId', '==', recordId),
      orderBy('at', 'desc'),
      limit(PAGE),
    ),
    (snap) => onData(snap.docs.map(mapNote)),
    (err) => onError(err as Error),
  )
}

/**
 * Append a note. There is deliberately no update and no delete.
 *
 * The rules refuse both as well, so this is not merely an omission: a note
 * records what somebody knew at a time, and editing one rewrites history
 * instead of correcting it. A mistake is answered with another note.
 */
export async function addNote(
  kind: NoteKind,
  recordId: string,
  author: string,
  text: string,
): Promise<void> {
  const trimmed = text.trim()
  if (!trimmed) throw new Error('An empty note has nothing to record.')
  if (trimmed.length > NOTE_MAX) {
    throw new Error(`That note is ${trimmed.length} characters; the limit is ${NOTE_MAX}.`)
  }
  await addDoc(collection(db, COLLECTION), {
    kind,
    recordId,
    author,
    text: trimmed,
    source: 'user',
    // The server's clock, not the browser's: two people's notes have to be
    // orderable against each other, and a laptop with a wrong clock would file
    // its notes in the wrong year.
    at: serverTimestamp(),
  })
}

/**
 * Carry a record's old prose `notes` field into the log as its first entry — 91.
 *
 * Written with `source: 'migrated'` and the record's own name as the author,
 * because nobody knows who wrote it or when: the field held one string with no
 * history. Marking it is what stops it looking like a note somebody signed.
 *
 * Idempotent by the caller's check, not by this function: the caller knows
 * whether the log already has the migrated entry, and re-running this would
 * otherwise duplicate it.
 */
export async function migrateProseNote(
  kind: NoteKind,
  recordId: string,
  text: string,
): Promise<void> {
  const trimmed = text.trim()
  if (!trimmed) return
  await addDoc(collection(db, COLLECTION), {
    kind,
    recordId,
    author: 'Before notes were a log',
    text: trimmed.slice(0, NOTE_MAX),
    source: 'migrated',
    at: serverTimestamp(),
  })
}
